'use client';

import { IconCloseFilled } from '@zillow/constellation-icons/react/filled/icon-close-filled';
import { type ElementType, Fragment, forwardRef } from 'react';

import { css, cx } from '@/styled-system/css';

import { useDisabledInteractions } from '../../hooks/use-disabled-interactions';
import { isEmptyReactNode } from '../../utils/is-empty-react-node';
import { Icon } from '../icon/icon';
import { Slot, Slottable } from '../slot';
import { VisuallyHidden } from '../visually-hidden/visually-hidden';
import { closeButtonRecipe } from './styles/close-button.styles';
import type { CloseButtonPropsInterface } from './types/close-button.types';

export const CloseButton = forwardRef<HTMLButtonElement, CloseButtonPropsInterface>(
  (props: CloseButtonPropsInterface, forwardedRef) => {
    const {
      asChild = false,
      children,
      disabled = false,
      className,
      css: cssProp,
      onImpact = false,
      label,
      title = 'Close',
      ...rest
    } = props;

    const styles = css(closeButtonRecipe.raw({ disabled, onImpact }), cssProp);

    const Component: ElementType = asChild ? Slot : 'button';

    // Real `<button>` uses the native `disabled` attribute; when rendered as an
    // anchor via `asChild`, fall back to aria-disabled + tabIndex + event blocking.
    const disabledInteractionsProps = useDisabledInteractions({ focusable: false });

    return (
      <Component
        data-c11n-component="CloseButton"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', 'c11n_close-button', styles, className)}
        ref={forwardedRef}
        title={label ?? title}
        disabled={!asChild && disabled ? true : undefined}
        type={asChild ? undefined : 'button'}
        {...rest}
        {...(asChild && disabled ? disabledInteractionsProps : {})}
      >
        <Slottable child={children}>
          {(slottable) => (
            <Fragment>
              <VisuallyHidden>{label ?? title}</VisuallyHidden>
              {isEmptyReactNode(slottable) ? <Icon render={<IconCloseFilled />} /> : slottable}
            </Fragment>
          )}
        </Slottable>
      </Component>
    );
  },
);

CloseButton.displayName = 'CloseButton';
